// オペレーション履歴タブ (AC-BSP-038) の行モデル・フィルタ・件数。
//
// 1 行 = ログ行の `lines` の 1 要素。 同じログ行 (= 同一バッチ) の行は連続して並ぶ。
// ログ行は操作した時点の札の値を持つので、 札を後から編集・削除しても表示は変わらない。
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingBlockBidGroupType,
  BiddingBlockBidType,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingSpotOrderMethod } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import {
  BiddingBidOperationType,
  type BiddingSpotOperationLog,
  BiddingSpotOperationLogBidKind,
  type BiddingSpotOperationLogLine,
} from '@ebalance/proto/bffuser/v1/bidding_spot_operation_log_model_pb'

import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'
import { formatBlockVolumeLabel } from '~/pages/SpotBidding/models/blockVolumeLabel'

export type OperationHistoryBidKind = 'normal' | 'block'

/** 記録する操作の種別 (6 種固定。 表示名は i18n `page:biddingSpot.operationHistory.operation.*`)。 */
export type OperationHistoryOperation =
  | 'cancel'
  | 'reopen'
  | 'withdraw'
  | 'reservationCancel'
  | 'returnToDraft'
  | 'jepxReconcile'

export const OPERATION_HISTORY_OPERATIONS: readonly OperationHistoryOperation[] =
  [
    'cancel',
    'reopen',
    'withdraw',
    'reservationCancel',
    'returnToDraft',
    'jepxReconcile',
  ]

/** 操作者をシステムとして出す操作 (backend は操作者を記録しない。 AC-BSP-117、 #12323)。 */
export const isSystemOperation = (
  operation: OperationHistoryOperation,
): boolean => operation === 'jepxReconcile'

export const OPERATION_HISTORY_BID_KINDS: readonly OperationHistoryBidKind[] = [
  'normal',
  'block',
]

/** ブロック種別 (ログ行の block_group_type)。 */
export type OperationHistoryBlockType = 'STANDARD' | 'LINK' | 'LOOP'

/** Leg のサブバッジ (STANDARD の Leg は出さない)。 */
export type OperationHistoryLegRole = 'LINK-P' | 'LINK-C' | 'LOOP-A' | 'LOOP-B'

export type OperationHistoryPrice =
  | { kind: 'market' }
  | { kind: 'limit'; yenPerKwh: string }
  | { kind: 'none' }

export type OperationHistoryRow = {
  /** 表示行の key (= `${batchId}:${lineIndex}`)。 */
  key: string
  /** バッチ ID (= ログ行の ID)。 同じバッチの行は同じ値。 */
  batchId: string
  bidKind: OperationHistoryBidKind
  operation: OperationHistoryOperation
  operatedAt: Date | undefined
  /** 操作者の member ID (表示名への解決は描画側)。 */
  operatedBy: string | undefined
  reason: string | undefined
  blockType: OperationHistoryBlockType | undefined
  legRole: OperationHistoryLegRole | undefined
  /** ブロックの Leg が同じバッチ内で何本目か / 何本あるか (通常入札・単独 Leg は groupSize 1)。 */
  groupPos: number
  groupSize: number
  areaCode: AreaCode
  direction: Side
  /** 時間帯 (例: `10:00〜11:00`。 コマ範囲の開始〜終了)。 */
  timeLabel: string
  price: OperationHistoryPrice
  /** 数量 (MW) の表示 (コマ別数量が違えば `50.0〜100.0 (可変)`)。 無ければ undefined。 */
  volumeLabel: string | undefined
  jepxBidNo: string | undefined
  deliveryContractCode: string | undefined
  workflowId: string | undefined
}

const OPERATION_BY_PROTO: Partial<
  Record<BiddingBidOperationType, OperationHistoryOperation>
> = {
  [BiddingBidOperationType.CANCEL]: 'cancel',
  [BiddingBidOperationType.REOPEN]: 'reopen',
  [BiddingBidOperationType.WITHDRAW]: 'withdraw',
  [BiddingBidOperationType.RESERVATION_CANCEL]: 'reservationCancel',
  [BiddingBidOperationType.RETURN_TO_DRAFT]: 'returnToDraft',
  [BiddingBidOperationType.JEPX_RECONCILE]: 'jepxReconcile',
}

const BLOCK_TYPE_BY_GROUP: Partial<
  Record<BiddingBlockBidGroupType, OperationHistoryBlockType>
> = {
  [BiddingBlockBidGroupType.BLOCK]: 'STANDARD',
  [BiddingBlockBidGroupType.LINK_BLOCK]: 'LINK',
  [BiddingBlockBidGroupType.LOOP_BLOCK]: 'LOOP',
}

const LEG_ROLE_BY_TYPE: Partial<
  Record<BiddingBlockBidType, OperationHistoryLegRole>
> = {
  [BiddingBlockBidType.LINK_BLOCK_P]: 'LINK-P',
  [BiddingBlockBidType.LINK_BLOCK_C]: 'LINK-C',
  [BiddingBlockBidType.LOOP_BLOCK_A]: 'LOOP-A',
  [BiddingBlockBidType.LOOP_BLOCK_B]: 'LOOP-B',
}

/** コマ範囲の時間帯表記 (`HH:MM〜HH:MM`。 最終コマは 24:00 で止める)。 */
export const operationTimeLabel = (from: number, to: number): string =>
  `${komaNumberToTime(from)}〜${komaEndTime(komaNumberToTime(to))}`

const priceOf = (line: BiddingSpotOperationLogLine): OperationHistoryPrice => {
  if (line.orderMethod === BiddingSpotOrderMethod.MARKET) {
    return { kind: 'market' }
  }
  const value = line.priceYenPerKwh?.value
  if (value === undefined || value === '') return { kind: 'none' }
  return { kind: 'limit', yenPerKwh: Number(value).toFixed(2) }
}

const volumeLabelOf = (
  line: BiddingSpotOperationLogLine,
): string | undefined => {
  const volumes = line.slotVolumes.map((v) => Number(v.volumeMw?.value ?? '0'))
  return volumes.length === 0 ? undefined : formatBlockVolumeLabel(volumes, 0)
}

const orUndefined = (value: string | undefined): string | undefined =>
  value === undefined || value === '' ? undefined : value

/**
 * ログ行の配列を表示行にする。 並びは受け取った順 (backend が新しい順で返す) のまま、
 * 1 つのログ行の `lines` を連続して並べる。 操作の種別が未知のログ行は出さない。
 */
export const toOperationHistoryRows = (
  logs: readonly BiddingSpotOperationLog[],
): OperationHistoryRow[] =>
  logs.flatMap((log) => {
    const operation = OPERATION_BY_PROTO[log.operationType]
    if (operation === undefined) return []
    const isBlock = log.bidKind === BiddingSpotOperationLogBidKind.BLOCK
    const blockType = isBlock
      ? BLOCK_TYPE_BY_GROUP[log.blockGroupType]
      : undefined
    return log.lines.map(
      (line, index): OperationHistoryRow => ({
        key: `${log.id}:${index}`,
        batchId: log.id,
        bidKind: isBlock ? 'block' : 'normal',
        operation,
        operatedAt:
          log.operatedAt === undefined
            ? undefined
            : timestampDate(log.operatedAt),
        operatedBy: orUndefined(log.operatedBy),
        reason: orUndefined(log.reason),
        blockType,
        legRole: isBlock ? LEG_ROLE_BY_TYPE[line.blockBidType] : undefined,
        groupPos: isBlock ? index : 0,
        groupSize: isBlock ? log.lines.length : 1,
        areaCode: line.areaCode,
        direction: line.direction,
        timeLabel: operationTimeLabel(line.timeCodeFrom, line.timeCodeTo),
        price: priceOf(line),
        volumeLabel: volumeLabelOf(line),
        jepxBidNo: orUndefined(line.jepxBidNo),
        deliveryContractCode: orUndefined(line.deliveryContractCode),
        workflowId: orUndefined(line.workflowId),
      }),
    )
  })

/**
 * 表に実際に出す行 (業務エリアとフィルタで絞った後) から、 ブロックのグループ位置
 * (groupPos / groupSize) を求め直す。 LOOP の 2 Leg は別エリアになりうるし、 売買などの
 * フィルタで片方の Leg だけが残ることもある。 その時は残った Leg が単独の行 (groupSize 1)
 * になり、 グループの罫線が崩れない。 通常入札の行は常に単独。
 */
export const regroupOperationHistoryRows = (
  rows: readonly OperationHistoryRow[],
): OperationHistoryRow[] => {
  const sizes = new Map<string, number>()
  for (const row of rows) {
    if (row.bidKind === 'block') {
      sizes.set(row.batchId, (sizes.get(row.batchId) ?? 0) + 1)
    }
  }
  const seen = new Map<string, number>()
  return rows.map((row) => {
    if (row.bidKind !== 'block') return { ...row, groupPos: 0, groupSize: 1 }
    const pos = seen.get(row.batchId) ?? 0
    seen.set(row.batchId, pos + 1)
    return { ...row, groupPos: pos, groupSize: sizes.get(row.batchId) ?? 1 }
  })
}

/** タブ内のフィルタ。 どの軸も空集合は「すべて」。 */
export type OperationHistoryFilter = {
  kinds: ReadonlySet<OperationHistoryBidKind>
  operations: ReadonlySet<OperationHistoryOperation>
  areas: ReadonlySet<AreaCode>
  sides: ReadonlySet<Side>
}

export const EMPTY_OPERATION_HISTORY_FILTER: OperationHistoryFilter = {
  kinds: new Set(),
  operations: new Set(),
  areas: new Set(),
  sides: new Set(),
}

const matchesAll = <T>(selected: ReadonlySet<T>, value: T): boolean =>
  selected.size === 0 || selected.has(value)

export const filterOperationHistoryRows = (
  rows: readonly OperationHistoryRow[],
  filter: OperationHistoryFilter,
): OperationHistoryRow[] =>
  rows.filter(
    (row) =>
      matchesAll(filter.kinds, row.bidKind) &&
      matchesAll(filter.operations, row.operation) &&
      matchesAll(filter.areas, row.areaCode) &&
      matchesAll(filter.sides, row.direction),
  )

export const isOperationHistoryFiltered = (
  filter: OperationHistoryFilter,
): boolean =>
  filter.kinds.size +
    filter.operations.size +
    filter.areas.size +
    filter.sides.size >
  0

export type OperationHistoryCount =
  | { kind: 'all'; total: number }
  | { kind: 'filtered'; shown: number; total: number }

/** 見出しの件数 (`(N件)` / フィルタ中は `(N件 / 全 M件)`)。 */
export const operationHistoryCount = (
  shown: number,
  total: number,
  filter: OperationHistoryFilter,
): OperationHistoryCount =>
  isOperationHistoryFiltered(filter)
    ? { kind: 'filtered', shown, total }
    : { kind: 'all', total }
